import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { CategoryType, IChecklist, OnlineOpcodeSortBy, SortOrder } from '@signal/asp-data-commons';
import { Observable, Subject } from 'rxjs';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { SubscriptionList } from '../../shared/models/asp.types';
import { ChecklistState } from '../../store/checklist/checklist.reducer';
import * as fromActions from '../../store/checklist/checklist.actions';
import * as fromSelector from '../../store/checklist/checklist.selectors';
import _ from 'lodash';
import { unsubscribeSubscriptions } from '../../shared/services/util.service';
import { exitConfig } from '../../shared/services/util.service';

@Component({
  selector: 'app-checklist-setup',
  templateUrl: './checklist-setup.component.html',
  styleUrls: ['./checklist-setup.component.scss']
})
export class ChecklistSetupComponent implements OnInit, OnDestroy {
  checkListForm: UntypedFormGroup;
  private subs: SubscriptionList = {};
  aspModalContainer: string = 'asp-modal-container';

  allChecklist$: Observable<IChecklist[]>;
  allChecklist: IChecklist[] = [];
  filteredAllChecklist: IChecklist[] = [];
  formattedAllChecklist: any;
  onlineChecklist$: Observable<IChecklist[]>;
  onlineChecklist: IChecklist[] = [];
  formattedOnlineChecklist: any;
  allChecklistLoading$: Observable<boolean>;
  selectedChecklistFromAllCategory: any = {};
  selectedChecklistFromOnlineCategory: any = {};
  selectedChecklistFromAll: IChecklist[] = [];
  selectedChecklistFromOnline: IChecklist[] = [];
  allChecklistCount$: Observable<number>;
  onlineChecklistCount$: Observable<number>;
  selectedChecklist: string;
  isAllChecklistExpandAll = false;
  isOnlineChecklistExpandAll = false;
  allItemWithoutDuration = 0;
  formsEdited=true;
  saveClicked=false;

  categories = [{ id: CategoryType.TECHNOLOGY, display: 'checklist.technology' }, 
  { id: CategoryType.GENERAL_FEATURES, display: 'checklist.generalFeatures' },
  { id: CategoryType.SAFETY, display: 'checklist.safety' },
  { id: CategoryType.VEHICLE_PUBLICATIONS, display: 'checklist.vehiclePublications' },
  { id: CategoryType.RECOMMENDED, display: 'opcodeServiceManagement.recommended' },
  { id: CategoryType.ACCESSORIES, display: 'checklist.accessories' }, 
  { id: CategoryType.DEALER_STAFF_INTRODUCTIONS, display: 'checklist.dealerStaffIntroductions' }]

  categoriesTranslation = {
    Technology : 'checklist.technology',
    'General Features' : 'checklist.generalFeatures',
    Safety : 'checklist.safety',
    'Vehicle Publications' : 'checklist.vehiclePublications',
    'Recommended': 'opcodeServiceManagement.recommended',
    'Accessories' : 'checklist.accessories', 
    'Dealer Staff Introductions' : 'checklist.dealerStaffIntroductions'
  }


  private readonly removeCustomSortConfig = {
    width: '400px',
    height: '240px',
    data: {
      popupTitle: 'opcodeServiceManagement.confirmation',
      popupBodyText: 'opcodeServiceManagement.changingTheOnlineSortingToAlphabeticalOrderWillResetAllCustomSortSettingsPreviouslyConfigured.DoYouWishToContinue?'
    },
    panelClass: this.aspModalContainer
  };

  constructor(
    private readonly fb: UntypedFormBuilder,
    public readonly dialog: MatDialog,
    private readonly checklistStore: Store<ChecklistState>,
    private cdr:ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.checklistStore.dispatch(new fromActions.LoadAllChecklist());

    this.allChecklist$ = this.checklistStore.select(fromSelector.selectAllChecklist);
    this.onlineChecklist$ = this.checklistStore.select(fromSelector.selectOnlineChecklist);
    this.allChecklistLoading$ = this.checklistStore.select(fromSelector.selectAllChecklistLoading);
    this.allChecklistCount$ = this.checklistStore.select(fromSelector.selectAllChecklistCount);
    this.onlineChecklistCount$ = this.checklistStore.select(fromSelector.selectOnlineChecklistCount);
    this.subs.allChecklist = this.allChecklist$.subscribe(list => {
      this.allChecklist = list;
      this.filteredAllChecklist = list;
      this.allItemWithoutDuration = list.filter(x => !x.duration).length;
      this.formattedAllChecklist = this.getSortedChecklist(this.filteredAllChecklist, this.sortBy, this.sortOrder);
    })
    this.subs.onlineChecklist = this.onlineChecklist$.subscribe(list => {
      this.onlineChecklist = list;
      this.formattedOnlineChecklist = this.getSortedChecklist(this.onlineChecklist, this.onlineSortBy, this.onlineSortOrder);
    })
  }
  canExit():Observable<boolean> { 
    const result = new Subject<boolean>();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, exitConfig
    );
    this.subs.dialogRef = dialogRef.afterClosed().subscribe(event => {
      if (event.action === 'confirm') {
        this.saveClicked=true;
        if (!this.cdr['destroyed']) {
          this.cdr.detectChanges();
        }
        result.next(true);
        result.complete();
      }
      else if(event.action === 'close'){
        result.next(true);
        result.complete();
      }
    });
    return result.asObservable(); 
  };
  
   // returns if the forms are edited
   formsNotEdited()
   {
     return this.formsEdited; 
   }

   valuesChanged(event)
   {
    this.formsEdited=event;
   }

  initializeForm() {
    this.checkListForm = this.fb.group({
      allChecklistForm: this.fb.group({
        searchQuery: [''],
        sortBy: ['alpha'],
        sortOrder: [SortOrder.ASC]
      }),
      onlineChecklistForm: this.fb.group({
        show: ['all'],
        sortBy: [OnlineOpcodeSortBy.ALPHA],
        sortOrder: [SortOrder.ASC]
      })
    });

    this.subs.searchQueryfromform = this.allChecklistForm.get('searchQuery').valueChanges.subscribe((t: string) => {
      this.filteredAllChecklist = this.allChecklist.filter(x => x.displayName?.toLowerCase().includes(t.trim().toLowerCase()) ||
       x.description?.toLowerCase().includes(t.trim().toLowerCase()));
      this.formattedAllChecklist = this.getSortedChecklist(this.filteredAllChecklist, this.sortBy, this.sortOrder);
    });

    this.subs.allsortbyfromform = this.allChecklistForm.get('sortBy').valueChanges.subscribe(t => {
      this.formattedAllChecklist = this.getSortedChecklist(this.filteredAllChecklist, this.sortBy, this.sortOrder);
    });

    this.subs.onlinesortbyfromform = this.onlineChecklistForm.get('sortBy').valueChanges.subscribe(t => {
      if (t === OnlineOpcodeSortBy.ALPHA) {
        this.removeCustomSortConfirmation();
      }
    });
  }

  removeCustomSortConfirmation() {
    
  }

  get allChecklistForm() {
    return this.checkListForm.get('allChecklistForm') as UntypedFormGroup;
  }

  get sortOrder() {
    return this.allChecklistForm.get('sortOrder').value;
  }

  get onlineSortOrder() {
    return this.onlineChecklistForm.get('sortOrder').value;
  }

  get sortBy() {
    return this.allChecklistForm.get('sortBy').value;
  }

  get onlineSortBy() {
    return this.onlineChecklistForm.get('sortBy').value;
  }

  get onlineChecklistForm() {
    return this.checkListForm.get('onlineChecklistForm') as UntypedFormGroup;
  }

  get onlineShowChecklist() {
    return this.onlineChecklistForm.get('show').value;
  }

  onAllSortOrderChange() {
    this.allChecklistForm.get('sortOrder').setValue(this.allChecklistForm.get('sortOrder').value === SortOrder.ASC ? 
    SortOrder.DESC : SortOrder.ASC);
    this.formattedAllChecklist = this.getSortedChecklist(this.filteredAllChecklist, this.sortBy, this.sortOrder);
  }

  onOnlineSortOrderChange() {
    this.onlineChecklistForm.get('sortOrder').setValue(this.onlineChecklistForm.get('sortOrder').value === SortOrder.ASC ? 
    SortOrder.DESC : SortOrder.ASC);
    this.formattedOnlineChecklist = this.getSortedChecklist(this.onlineChecklist, this.onlineSortBy, this.onlineSortOrder);
  }

  selectionChanged(ev, list, category: string) {
    if (list === 1) {
      this.selectedChecklistFromAllCategory[category] = ev;
      this.selectedChecklistFromAll = this.getSelectedChecklist(this.selectedChecklistFromAllCategory);
    }
    else if (list === 2) {
      this.selectedChecklistFromOnlineCategory[category] = ev;
      this.selectedChecklistFromOnline = this.getSelectedChecklist(this.selectedChecklistFromOnlineCategory);
    }

    if (!!ev[0] && !!ev[0].checklistId) {
      this.selectedChecklist = (ev.length === 1) ? ev[0].checklistId : null;
    }
  }

  updateChecklistStatus(status: boolean) {
    if ((this.selectedChecklistFromAll.length > 0 === status) ||
      (this.selectedChecklistFromOnline.length > 0 === !status)) {
      for (const checklist of (status ? this.selectedChecklistFromAll : this.selectedChecklistFromOnline)) {
        if (status !== checklist.status) {
          this.checklistStore.dispatch(new fromActions.UpdateChecklist({ checklist: { ...checklist, status } }));
        }
      }
    }
  }

  getCategories = (checklist: any, isSort = false, from = 'all') => {
    let keys = [];
    if (checklist) {
      keys = Object.keys(checklist);
      if (isSort) {
        const sort = from === 'all' ? this.allChecklistForm.get('sortBy').value :
          this.onlineChecklistForm.get('sortBy').value;
        const order = from === 'all' ? this.sortOrder :
          this.onlineSortOrder;
        if (sort === 'alpha') {
          keys.sort((a: string, b: string) => {
            return order === SortOrder.ASC ? a.localeCompare(b) :
              b.localeCompare(a);
          })
        }
      }
    }
    return keys;
  }

  getSelectedChecklist(checklist: any) {
    const keys = this.getCategories(checklist);
    let list = [];
    if (keys.length) {
      keys.forEach(key => {
        list.push(...checklist[key])
      })
    }
    return list;
  }

  getSortedChecklist(checklist: IChecklist[], sort: string, order: string) {
    let checklistItems = [...checklist];
    if (sort === 'alpha') {
      checklistItems.sort((a: IChecklist, b: IChecklist) => {
        return order === SortOrder.ASC ? a.displayName.localeCompare(b.displayName) :
          b.displayName.localeCompare(a.displayName);
      })
    }
    else if (sort === 'duration') {
      checklistItems.sort((a: IChecklist, b: IChecklist) => {
        return order === SortOrder.ASC ? a?.duration - b?.duration :
          b?.duration - a?.duration;
      })
    }
    return _.groupBy(checklistItems, 'category');
  }

  isAllHaveDuration(checklist: IChecklist[]) {
    return checklist.every(x=> x.duration);
  }

  isSelectedItemValid(ev){
    return ev.every((item) => item.checklistId && item.displayName && item.duration && item.category);
  }

  ngOnDestroy() {
    unsubscribeSubscriptions(this.subs);
    this.cdr.detach();
  }

}

